import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { STATUS_FINISHED, STATUS_STARTED } from '../util';

export const stepContext = React.createContext({});

export const StepProvider = ({
  slug,
  status,
  fetchedAnswers,
  setFetchedAnswers,
  assessment,
  checkData,
  setCheckData,
  metadata,
  api,
  components,
  startNow,
  answers,
  setAnswers,
  screenData,
  setScreenData,
  holdStep,
  setHoldStep,
  complete,
  setComplete,
  invitation,
  restartAssessment,
  userAssessmentId,
  setUserAssessmentId,
  children
}) => {
  const [step, setStep] = useState(0);
  const [sequence, setSequence] = useState(null);
  const [history, setHistory] = useState([]);
  const [reviewing, setReviewing] = useState(false);
  const [dependentQuestionList, setDependentQuestionList] = useState([]);
  const router = useRouter();

  const total = sequence?.length || 0;
  const hasPreviousStep = step > 0;

  useEffect(() => {
    setStep(0);
    setSequence(null);
    setHistory([]);
    setReviewing(false);
    setAnswers({});
  }, [slug]);

  useEffect(() => {
    if (checkData?.status !== STATUS_FINISHED && step === total - 1) {
      handleRestart();
    } else {
      setComplete(checkData?.status === STATUS_FINISHED);
    }
  }, [checkData?.status]);

  const navigate = (toStep) => {
    setStep(toStep);
  };

  const resetAnswers = (options = {}) => {
    setAnswers({ ...(options.startingAnswers || {}) });
    if (!options.preserveFetchedAnswers) {
      setFetchedAnswers({});
    }
    setReviewing(false);
  };

  const handleRestart = async ({ user_assessment_id = null } = {}) => {
    if (user_assessment_id) {
      setUserAssessmentId(user_assessment_id);
    } else {
      await restartAssessment();
    }
    navigate(0);
    resetAnswers();
    setFetchedAnswers({});
    setHistory([]);
    setComplete(null);
    setCheckData((prevState) => ({ ...prevState, status: STATUS_STARTED }));
  };

  const goBack = () => {
    history.pop();
    let prev = history[history.length - 1];
    if (prev === undefined) {
      prev = 0;
    }
    navigate(prev);
    setHistory(history);
    return true;
  };

  const goToQuestion = (question_id) => {
    navigate(sequence.findIndex((seq) => seq.type === 'question' && seq.value === question_id));
    setReviewing(true);
  };

  const goToStep = (nextStep, resetReviewing) => {
    setReviewing(!!resetReviewing);
    navigate(nextStep);
    setHistory((prevState) => [...prevState, nextStep]);
  };

  const exit = () => {
    router.push('/plan#assessments'); // TODO allow the exit path to be passed in as an option
  };

  const getAnswerForQuestion = (currentAnswer, questionId, defaultValue) => {
    // TODO skipping an already answered question doesn't clear its displayed value
    if (typeof currentAnswer !== 'undefined') {
      return typeof currentAnswer.value !== 'undefined' ? currentAnswer.value : currentAnswer;
    }
    const answerObj = (fetchedAnswers || {})[questionId] || undefined;
    return typeof answerObj?.value === 'undefined' || !metadata.options?.prefill_incomplete_assessment ? defaultValue || undefined : answerObj?.value; // account for '' or 0
  };

  return (
    <stepContext.Provider
      value={{
        slug,
        status,
        step,
        total,
        reviewing,
        complete,
        setComplete,
        goToStep,
        sequence,
        setSequence,
        handleRestart,
        goBack,
        goToQuestion,
        getAnswerForQuestion,
        assessment,
        userAssessmentId,
        setUserAssessmentId,
        checkData,
        setCheckData,
        metadata,
        api,
        components,
        exit,
        fetchedAnswers,
        answers,
        setAnswers,
        resetAnswers,
        screenData,
        setScreenData,
        holdStep,
        setHoldStep,
        invitation,
        dependentQuestionList,
        setDependentQuestionList,
        hasPreviousStep,
        setHistory
      }}
    >
      {children}
    </stepContext.Provider>
  );
};

export const StepConsumer = stepContext.Consumer;
